const STORAGE_KEY = "nx_track_session";
const ATTRIB_KEY = "nx_track_attrib";
const MATCH_KEY = "nx_track_match";
const SESSION_TTL_MS = 30 * 60 * 1000;

type Attribution = {
  utmSource?: string; utmMedium?: string; utmCampaign?: string;
  utmTerm?: string; utmContent?: string;
  referrer?: string; landingPath?: string; firstSeenAt: number;
  // Timestamp of the LAST time a utm_campaign was actually seen in the URL
  // (last-touch). Used to expire stale campaign attribution so a campaign a
  // buyer clicked weeks ago is not credited for unrelated future orders.
  lastCampaignAt?: number;
};

// Last-touch campaign attribution window. Orders placed more than this long
// after the buyer last arrived via a ?utm_campaign link are NOT attributed to
// that campaign (the server session-events fallback still applies within the
// active tracking session).
const CAMPAIGN_ATTRIB_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

type SessionState = { id: string; lastActiveAt: number };

export type AdvancedMatch = {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  country?: string;
  zip?: string;
  externalId?: string;
};

function genId(): string {
  const a = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(a);
  else for (let i = 0; i < 16; i++) a[i] = Math.floor(Math.random() * 256);
  return Array.from(a, b => b.toString(16).padStart(2, "0")).join("");
}

function readSession(): SessionState {
  try {
    const cartSid = localStorage.getItem("openstore_cart_session");
    if (cartSid) return { id: cartSid, lastActiveAt: Date.now() };
  } catch {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const s = JSON.parse(raw) as SessionState;
      if (s.id && Date.now() - s.lastActiveAt < SESSION_TTL_MS) return s;
    }
  } catch {}
  const fresh = { id: genId(), lastActiveAt: Date.now() };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh)); } catch {}
  return fresh;
}

function touchSession(s: SessionState) {
  s.lastActiveAt = Date.now();
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
}

function readAttribution(): Attribution | null {
  try { const raw = localStorage.getItem(ATTRIB_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

function writeAttribution(a: Attribution) {
  try { localStorage.setItem(ATTRIB_KEY, JSON.stringify(a)); } catch {}
}

function captureAttributionFromUrl(): Attribution | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const has = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].some(k => params.get(k));
  const existing = readAttribution();
  if (!has && existing) return existing;
  const ref = document.referrer && !document.referrer.includes(window.location.host) ? document.referrer : "";
  if (!has && !existing && !ref) return null;
  const urlCampaign = params.get("utm_campaign") || undefined;
  const attr: Attribution = {
    utmSource: params.get("utm_source") || (ref ? new URL(ref).hostname : undefined) || undefined,
    utmMedium: params.get("utm_medium") || (ref ? "referral" : undefined) || undefined,
    utmCampaign: urlCampaign,
    utmTerm: params.get("utm_term") || undefined,
    utmContent: params.get("utm_content") || undefined,
    referrer: ref || undefined,
    landingPath: window.location.pathname + window.location.search,
    firstSeenAt: existing?.firstSeenAt || Date.now(),
    // Refresh the last-touch stamp only when a campaign is actually present in
    // this URL; otherwise carry the previous one forward so the window measures
    // time since the buyer last clicked a campaign link.
    lastCampaignAt: urlCampaign ? Date.now() : existing?.lastCampaignAt,
  };
  writeAttribution(attr);
  return attr;
}

// Returns the campaign code to attribute an order to, but ONLY when the buyer
// arrived via a ?utm_campaign link within the attribution window. Returns null
// for stale attribution so unrelated future orders are not mis-credited; the
// server's session-events fallback then decides within the active session.
export function getAttributedCampaign(): string | null {
  const a = readAttribution();
  const code = a?.utmCampaign?.trim();
  if (!code) return null;
  const at = a?.lastCampaignAt ?? a?.firstSeenAt ?? 0;
  if (!at || Date.now() - at > CAMPAIGN_ATTRIB_WINDOW_MS) return null;
  return code;
}

export function setAdvancedMatch(m: AdvancedMatch | null) {
  try {
    if (!m) localStorage.removeItem(MATCH_KEY);
    else localStorage.setItem(MATCH_KEY, JSON.stringify(m));
  } catch {}
}
export function readAdvancedMatch(): AdvancedMatch | null {
  try { const raw = localStorage.getItem(MATCH_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

function normalizeForHash(s: string | undefined, kind: "email" | "phone" | "name"): string | undefined {
  if (!s) return undefined;
  let v = String(s).trim().toLowerCase();
  if (kind === "phone") v = v.replace(/[^\d+]/g, "");
  if (kind === "name") v = v.replace(/[^\p{L}\s'-]/gu, "");
  return v || undefined;
}
async function sha256Hex(s: string): Promise<string | undefined> {
  try {
    if (!crypto?.subtle) return undefined;
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
    return Array.from(new Uint8Array(buf), b => b.toString(16).padStart(2, "0")).join("");
  } catch { return undefined; }
}

async function buildHashedUserData(): Promise<Record<string, string | undefined>> {
  const m = readAdvancedMatch();
  if (!m) return {};
  const em = normalizeForHash(m.email, "email");
  const ph = normalizeForHash(m.phone, "phone");
  const fn = normalizeForHash(m.firstName, "name");
  const ln = normalizeForHash(m.lastName, "name");
  const ct = normalizeForHash(m.city, "name");
  const cy = normalizeForHash(m.country, "name");
  const zp = m.zip ? String(m.zip).trim().toLowerCase() : undefined;
  const [emH, phH, fnH, lnH, ctH, cyH, zpH] = await Promise.all([
    em ? sha256Hex(em) : undefined,
    ph ? sha256Hex(ph) : undefined,
    fn ? sha256Hex(fn) : undefined,
    ln ? sha256Hex(ln) : undefined,
    ct ? sha256Hex(ct) : undefined,
    cy ? sha256Hex(cy) : undefined,
    zp ? sha256Hex(zp) : undefined,
  ]);
  return { em: emH, ph: phH, fn: fnH, ln: lnH, ct: ctH, country: cyH, zp: zpH, external_id: m.externalId };
}

export type TrackPayload = {
  eventName: string;
  eventValue?: number;
  currency?: string;
  productId?: number;
  orderId?: number;
  meta?: Record<string, any>;
  contentIds?: (string | number)[];
  contentType?: "product" | "product_group";
  contents?: { id: string | number; quantity?: number; item_price?: number }[];
  searchString?: string;
  numItems?: number;
};

export function getTrackingSessionId(): string {
  return readSession().id;
}

const DEDUP_WINDOW_MS: Record<string, number> = {
  page_view: 2000,
  view_item: 30000,
  view_item_list: 30000,
  add_to_cart: 1500,
  add_to_wishlist: 1500,
  begin_checkout: 2000,
  add_payment_info: 2000,
  search: 1500,
};
const recentEvents = new Map<string, number>();
function shouldDedupe(eventName: string, productId?: number): boolean {
  const window = DEDUP_WINDOW_MS[eventName];
  if (!window) return false;
  const path = typeof location !== "undefined" ? location.pathname : "";
  const key = `${eventName}|${path}|${productId ?? ""}`;
  const now = Date.now();
  const last = recentEvents.get(key);
  if (last && now - last < window) return true;
  recentEvents.set(key, now);
  if (recentEvents.size > 200) {
    for (const [k, t] of recentEvents) {
      if (now - t > 60000) recentEvents.delete(k);
    }
  }
  return false;
}

export async function track(payload: TrackPayload): Promise<void> {
  if (typeof window === "undefined") return;
  if (isAdminPath(window.location.pathname)) return;
  if (shouldDedupe(payload.eventName, payload.productId)) return;
  const session = readSession();
  touchSession(session);
  const attr = captureAttributionFromUrl();
  const eventId = genId();
  const userData = await buildHashedUserData();
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc") || consumeFbclidToFbc();
  const body = {
    sessionId: session.id,
    eventName: payload.eventName,
    eventValue: payload.eventValue,
    currency: payload.currency,
    productId: payload.productId,
    orderId: payload.orderId,
    utmSource: attr?.utmSource,
    utmMedium: attr?.utmMedium,
    utmCampaign: attr?.utmCampaign,
    utmTerm: attr?.utmTerm,
    utmContent: attr?.utmContent,
    referrer: attr?.referrer,
    landingPath: attr?.landingPath,
    pagePath: window.location.pathname,
    meta: {
      ...(payload.meta || {}),
      eventId,
      contentIds: payload.contentIds,
      contentType: payload.contentType,
      contents: payload.contents,
      searchString: payload.searchString,
      numItems: payload.numItems,
      userData,
      fbp,
      fbc,
      sourceUrl: window.location.href,
    },
  };
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    const url = "/api/conversion-events";
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) {
      // queued
    } else {
      await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: true });
    }
  } catch {}
  forwardToPixels(payload, eventId);
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : undefined;
}
function consumeFbclidToFbc(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (!fbclid) return undefined;
    const subdomainIndex = 1;
    const fbc = `fb.${subdomainIndex}.${Date.now()}.${fbclid}`;
    document.cookie = `_fbc=${encodeURIComponent(fbc)}; path=/; max-age=${60 * 60 * 24 * 90}`;
    return fbc;
  } catch { return undefined; }
}

function forwardToPixels(p: TrackPayload, eventId: string) {
  const w = window as any;
  const value = p.eventValue ?? 0;
  const currency = p.currency ?? "SAR";
  const contentIds = (p.contentIds || (p.productId ? [p.productId] : [])).map(String);
  const contents = p.contents || (p.productId ? [{ id: p.productId, quantity: 1, item_price: p.eventValue }] : undefined);
  const numItems = p.numItems ?? (contents ? contents.reduce((n, c) => n + (c.quantity || 1), 0) : undefined);

  // Meta Pixel
  try {
    if (typeof w.fbq === "function") {
      const map: Record<string, string> = {
        page_view: "PageView", view_item: "ViewContent", view_item_list: "ViewContent",
        add_to_cart: "AddToCart", begin_checkout: "InitiateCheckout", add_payment_info: "AddPaymentInfo",
        purchase: "Purchase", search: "Search", sign_up: "CompleteRegistration",
        login: "Lead", add_to_wishlist: "AddToWishlist",
      };
      const fbName = map[p.eventName];
      if (fbName) {
        const data: any = {};
        if (p.eventValue !== undefined) { data.value = value; data.currency = currency; }
        if (contentIds.length) { data.content_ids = contentIds; data.content_type = p.contentType || "product"; }
        if (contents) data.contents = contents;
        if (numItems !== undefined) data.num_items = numItems;
        if (p.searchString) data.search_string = p.searchString;
        w.fbq("track", fbName, data, { eventID: eventId });
      }
    }
  } catch {}

  // GA4 (gtag)
  try {
    if (typeof w.gtag === "function") {
      const items = contents?.map(c => ({ item_id: String(c.id), quantity: c.quantity, price: c.item_price })) ||
        (p.productId ? [{ item_id: String(p.productId), quantity: 1, price: p.eventValue }] : undefined);
      w.gtag("event", p.eventName, {
        value, currency, transaction_id: p.orderId ? String(p.orderId) : undefined,
        items, search_term: p.searchString, ...(p.meta || {}),
      });
    }
  } catch {}

  // GTM dataLayer
  try {
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({
        event: p.eventName, value, currency,
        content_ids: contentIds, contents,
        order_id: p.orderId, search_string: p.searchString, event_id: eventId,
      });
    }
  } catch {}

  // TikTok
  try {
    if (typeof w.ttq === "object" && w.ttq && typeof w.ttq.track === "function") {
      const map: Record<string, string> = {
        view_item: "ViewContent", add_to_cart: "AddToCart",
        begin_checkout: "InitiateCheckout", add_payment_info: "AddPaymentInfo",
        purchase: "CompletePayment", search: "Search", sign_up: "CompleteRegistration",
        add_to_wishlist: "AddToWishlist",
      };
      const tName = map[p.eventName];
      if (tName) {
        const data: any = { value, currency };
        if (contentIds.length) data.contents = contentIds.map((id, i) => ({
          content_id: id, content_type: "product",
          quantity: contents?.[i]?.quantity ?? 1, price: contents?.[i]?.item_price ?? p.eventValue,
        }));
        if (p.searchString) data.query = p.searchString;
        w.ttq.track(tName, data, { event_id: eventId });
      }
    }
  } catch {}

  // Pinterest
  try {
    if (typeof w.pintrk === "function") {
      const map: Record<string, string> = {
        view_item: "pagevisit", add_to_cart: "addtocart",
        begin_checkout: "checkout", add_payment_info: "custom",
        purchase: "checkout",
        sign_up: "signup", search: "search", add_to_wishlist: "lead",
      };
      const pName = map[p.eventName];
      if (pName) {
        const data: any = { event_id: eventId };
        if (pName === "custom") data.custom_event_name = p.eventName;
        if (p.eventValue !== undefined) { data.value = value; data.currency = currency; }
        if (contentIds.length) {
          data.line_items = contentIds.map((id, i) => ({
            product_id: id, product_quantity: contents?.[i]?.quantity ?? 1,
            product_price: contents?.[i]?.item_price ?? p.eventValue,
          }));
        }
        if (numItems !== undefined) data.order_quantity = numItems;
        if (p.searchString) data.search_query = p.searchString;
        w.pintrk("track", pName, data);
      }
    }
  } catch {}

  // Snapchat
  try {
    if (typeof w.snaptr === "function") {
      const map: Record<string, string> = {
        view_item: "VIEW_CONTENT", add_to_cart: "ADD_CART",
        begin_checkout: "START_CHECKOUT", add_payment_info: "ADD_BILLING",
        purchase: "PURCHASE", search: "SEARCH",
        sign_up: "SIGN_UP", add_to_wishlist: "ADD_TO_WISHLIST",
      };
      const sName = map[p.eventName];
      if (sName) {
        const data: any = { client_dedup_id: eventId };
        if (p.eventValue !== undefined) { data.price = value; data.currency = currency; }
        if (contentIds.length) { data.item_ids = contentIds; data.item_category = "product"; }
        if (numItems !== undefined) data.number_items = numItems;
        if (p.searchString) data.search_string = p.searchString;
        if (p.orderId) data.transaction_id = String(p.orderId);
        w.snaptr("track", sName, data);
      }
    }
  } catch {}
}

function isAdminPath(p: string): boolean {
  return p.startsWith("/admin") || p.includes("/admin/");
}

// Auto-record a marketing tracking-link click. Fires only when the visitor
// actually landed with ?utm_campaign=<code> in the URL (i.e. clicked a tracking
// link), and only once per browser session per campaign (sessionStorage guard)
// so a page refresh or SPA navigation never inflates the click count.
function registerCampaignClick() {
  try {
    const code = new URLSearchParams(window.location.search).get("utm_campaign");
    if (!code || !code.trim()) return;
    const key = "nx_camp_click_" + code.trim().toLowerCase();
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const body = JSON.stringify({ code: code.trim() });
    const url = "/api/marketing-campaigns/track-click";
    const blob = new Blob([body], { type: "application/json" });
    if (!(navigator.sendBeacon && navigator.sendBeacon(url, blob))) {
      void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch {}
}

export function initTracking() {
  if (typeof window === "undefined") return;
  if (isAdminPath(window.location.pathname)) return;
  captureAttributionFromUrl();
  registerCampaignClick();
  track({ eventName: "page_view" });
  let lastPath = window.location.pathname;
  const onChange = () => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      if (!isAdminPath(lastPath)) track({ eventName: "page_view" });
    }
  };
  const origPush = history.pushState;
  const origRep = history.replaceState;
  history.pushState = function (...args: any[]) { const r = origPush.apply(this, args as any); onChange(); return r; };
  history.replaceState = function (...args: any[]) { const r = origRep.apply(this, args as any); onChange(); return r; };
  window.addEventListener("popstate", onChange);
}
